import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaCheckCircle } from "react-icons/fa";
import {
  batchDeleteStudents,
  getIndividualStudentResults,
  searchTeacherStudents
} from "../services/APIClient";
import "./TeacherRemoveChildModal.css";

const STUDENT_SEARCH_MIN_LENGTH = 2;
const STUDENT_SEARCH_LIMIT = 25;
const STUDENT_SEARCH_DEBOUNCE_MS = 300;
const OTHER_REASON = "Other reason";

const SCREENER_STATUS = {
  checking: "checking",
  hasResults: "has-results",
  noResults: "no-results",
  unavailable: "unavailable"
};

const REMOVE_REASONS = [
  "Moved to another class",
  "Left the school",
  "Added by mistake",
  OTHER_REASON
];

const normaliseName = (name) =>
  String(name || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const normaliseText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ");

const getMatchingReason = (reason) => {
  const normalisedReason = normaliseName(reason);

  return REMOVE_REASONS.find((reasonOption) => normaliseName(reasonOption) === normalisedReason) || "";
};

const normalisePrefill = (prefill) => {
  const studentName = normaliseText(prefill?.studentName || prefill?.student_name);
  const className = normaliseText(prefill?.className || prefill?.class_name);
  const reason = normaliseText(prefill?.reason);
  const note = normaliseText(prefill?.note);
  const matchingReason = getMatchingReason(reason);

  if (!reason) {
    return {
      studentName,
      className,
      removeReason: "",
      otherReason: ""
    };
  }

  if (matchingReason === OTHER_REASON) {
    return {
      studentName,
      className,
      removeReason: OTHER_REASON,
      otherReason: note
    };
  }

  if (matchingReason) {
    return {
      studentName,
      className,
      removeReason: matchingReason,
      otherReason: ""
    };
  }

  return {
    studentName,
    className,
    removeReason: OTHER_REASON,
    otherReason: note || reason
  };
};

const getPrefilledChildMatch = (children, prefill) => {
  if (!prefill.className) {
    return children[0] || null;
  }

  const matchingChildren = children.filter((child) =>
    [child.className, child.classDisplayName].some((value) =>
      normaliseName(value).includes(normaliseName(prefill.className))
    )
  );

  return matchingChildren.length === 1 ? matchingChildren[0] : children[0] || null;
};

const getKnownScreenerStatus = (child) => {
  if (typeof child?.hasScreenerResults === "boolean") {
    return child.hasScreenerResults ? SCREENER_STATUS.hasResults : SCREENER_STATUS.noResults;
  }

  if (typeof child?.hasScreener === "boolean") {
    return child.hasScreener ? SCREENER_STATUS.hasResults : SCREENER_STATUS.noResults;
  }

  return SCREENER_STATUS.checking;
};

const hasScreenerResults = (results) => Array.isArray(results) && results.length > 0;

const getScreenerStatusLabel = (status) => {
  if (status === SCREENER_STATUS.hasResults) {
    return "Has screener results";
  }

  if (status === SCREENER_STATUS.noResults) {
    return "No screener results";
  }

  if (status === SCREENER_STATUS.unavailable) {
    return "Could not check screener results";
  }

  return "Checking screener results";
};

const getScreenerSelectionWarning = (status, childName) => {
  const name = childName || "This child";

  if (status === SCREENER_STATUS.hasResults) {
    return `${name} has screener results. You can still continue, but check this is the correct child before removing them.`;
  }

  if (status === SCREENER_STATUS.unavailable) {
    return `We could not check screener results for ${name}. You can still continue, but check this is the correct child before removing them.`;
  }

  if (status === SCREENER_STATUS.checking) {
    return `Screener results are still being checked for ${name}. You can still continue, but check this is the correct child before removing them.`;
  }

  return "";
};

const getDuplicateChildName = (children) => {
  const nameCounts = children.reduce((counts, child) => {
    const normalisedName = normaliseName(child.name);

    if (!normalisedName) {
      return counts;
    }

    return {
      ...counts,
      [normalisedName]: (counts[normalisedName] || 0) + 1
    };
  }, {});

  const duplicateChild = children.find((child) => nameCounts[normaliseName(child.name)] > 1);

  return duplicateChild?.name || "";
};

const sortChildrenForRemoval = (children) => {
  const statusOrder = {
    [SCREENER_STATUS.noResults]: 0,
    [SCREENER_STATUS.hasResults]: 1,
    [SCREENER_STATUS.checking]: 2,
    [SCREENER_STATUS.unavailable]: 3
  };

  return [...children].sort((childA, childB) => {
    const statusComparison =
      (statusOrder[childA.screenerStatus] ?? 99) -
      (statusOrder[childB.screenerStatus] ?? 99);

    if (statusComparison !== 0) {
      return statusComparison;
    }

    return childA.classDisplayName.localeCompare(childB.classDisplayName);
  });
};

const normaliseChild = (child) => ({
  ...child,
  id: child?.id,
  name: child?.name || "Unnamed child",
  classId: child?.classId,
  className: child?.className || "Class unavailable",
  classDisplayName:
    child?.classDisplayName ||
    [child?.className, child?.grade, child?.academicYear]
      .filter(Boolean)
      .join(" - ") ||
    "Class unavailable",
  grade: child?.grade || "",
  academicYear: child?.academicYear || null,
  screenerStatus: getKnownScreenerStatus(child)
});

const getFirstName = (name) => (name || "This child").split(" ")[0];

function TeacherRemoveChildModal({
  show,
  handleClose,
  teacherid,
  teacherName,
  prefill = null,
  embedded = false,
  onWorkflowComplete = null
}) {
  const [stage, setStage] = useState("form");
  const [searchTerm, setSearchTerm] = useState("");
  const [matchingChildren, setMatchingChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [removeReason, setRemoveReason] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [error, setError] = useState("");
  const prefillContext = useMemo(() => normalisePrefill(prefill), [prefill]);
  const prefillKey = useMemo(() => JSON.stringify(prefillContext), [prefillContext]);

  const trimmedSearchTerm = searchTerm.trim();
  const duplicateChildName = useMemo(
    () => getDuplicateChildName(matchingChildren),
    [matchingChildren]
  );
  const isDisambiguating = Boolean(duplicateChildName);
  const selectedChildScreenerStatus = selectedChild?.screenerStatus || SCREENER_STATUS.checking;
  const selectedChildWarning = selectedChild
    ? getScreenerSelectionWarning(selectedChildScreenerStatus, selectedChild.name)
    : "";
  const effectiveRemoveReason =
    removeReason === OTHER_REASON ? otherReason.trim() : removeReason;
  const selectedChildFirstName = getFirstName(selectedChild?.name);
  const reasonSubject = selectedChild
    ? selectedChildFirstName
    : isDisambiguating
      ? getFirstName(duplicateChildName)
      : "this child";

  useEffect(() => {
    if (!show) {
      return;
    }

    setStage("form");
    setSearchTerm(prefillContext.studentName);
    setMatchingChildren([]);
    setSelectedChild(null);
    setRemoveReason(prefillContext.removeReason);
    setOtherReason(prefillContext.otherReason);
    setIsSearching(false);
    setIsRemoving(false);
    setError("");
  }, [show, prefillKey]);

  useEffect(() => {
    if (!show) {
      return undefined;
    }

    if (trimmedSearchTerm.length < STUDENT_SEARCH_MIN_LENGTH) {
      return undefined;
    }

    let isCurrentSearch = true;
    setIsSearching(true);
    setError("");

    const timeoutId = window.setTimeout(() => {
      searchTeacherStudents(trimmedSearchTerm, STUDENT_SEARCH_LIMIT)
        .then(async (children) => {
          if (!isCurrentSearch) {
            return;
          }

          const normalisedChildren = Array.isArray(children)
            ? children.map(normaliseChild)
            : [];
          const childrenWithScreenerStatus = await Promise.all(
            normalisedChildren.map(async (child) => {
              if (child.screenerStatus !== SCREENER_STATUS.checking) {
                return child;
              }

              try {
                const results = await getIndividualStudentResults(child.id);

                return {
                  ...child,
                  screenerStatus: hasScreenerResults(results)
                    ? SCREENER_STATUS.hasResults
                    : SCREENER_STATUS.noResults
                };
              } catch (resultsError) {
                console.error("Error checking child screener status:", resultsError);

                return {
                  ...child,
                  screenerStatus: SCREENER_STATUS.unavailable
                };
              }
            })
          );

          if (!isCurrentSearch) {
            return;
          }

          const sortedChildren = sortChildrenForRemoval(childrenWithScreenerStatus);

          setMatchingChildren(sortedChildren);
          setSelectedChild(getPrefilledChildMatch(sortedChildren, prefillContext));
        })
        .catch((searchError) => {
          if (!isCurrentSearch) {
            return;
          }

          console.error("Error searching teacher children:", searchError);
          setMatchingChildren([]);
          setError("Child search could not be loaded. Please try again.");
        })
        .finally(() => {
          if (isCurrentSearch) {
            setIsSearching(false);
          }
        });
    }, STUDENT_SEARCH_DEBOUNCE_MS);

    return () => {
      isCurrentSearch = false;
      window.clearTimeout(timeoutId);
    };
  }, [show, trimmedSearchTerm, prefillContext]);

  useEffect(() => {
    if (removeReason && !REMOVE_REASONS.includes(removeReason)) {
      setRemoveReason("");
      setOtherReason("");
    }
  }, [removeReason]);

  const canRemove = useMemo(
    () => selectedChild && effectiveRemoveReason && !isRemoving,
    [effectiveRemoveReason, isRemoving, selectedChild]
  );

  const handleChildSelect = (child) => {
    setSelectedChild(child);
    setError("");
  };

  const handleSearchChange = (event) => {
    const nextSearchTerm = event.target.value;
    setSearchTerm(nextSearchTerm);

    if (nextSearchTerm.trim().length < STUDENT_SEARCH_MIN_LENGTH) {
      setMatchingChildren([]);
      setSelectedChild(null);
      setIsSearching(false);
      setError("");
    } else if (
      selectedChild &&
      normaliseName(nextSearchTerm) !== normaliseName(selectedChild.name)
    ) {
      setSelectedChild(null);
    }
  };

  const removeChild = async () => {
    if (!canRemove) {
      return;
    }

    setIsRemoving(true);
    setError("");

    try {
      const response = await batchDeleteStudents({
        studentIds: [selectedChild.id],
        deletionReason: effectiveRemoveReason,
        deletedByTeacherId: teacherid?.toString() || "unknown",
        deletedByTeacherName: teacherName || "Unknown Teacher"
      });

      if (!response || response.deletedCount < 1) {
        setError("No child was removed. Please try again.");
        setStage("form");
        return;
      }

      setMatchingChildren((children) =>
        children.filter((child) => child.id !== selectedChild.id)
      );
      setStage("success");
    } catch (removeError) {
      console.error("Error removing child:", removeError);
      setError("The child could not be removed. Please try again.");
      setStage("form");
    } finally {
      setIsRemoving(false);
    }
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: selectedChild?.id || "",
        message: "Child removed."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const renderForm = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title>Remove Child</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="remove-child-intro">
          {isDisambiguating
            ? "Choose the right child from your available classes."
            : "Find the child and tell us why they should be removed."}
        </p>
        {error && <Alert variant="danger">{error}</Alert>}

        <Form.Group className="mb-3" controlId="removeChildSearch">
          <Form.Label>
            {isDisambiguating
              ? `Which ${duplicateChildName} should be removed?`
              : "Which child should be removed?"}
          </Form.Label>
          <Form.Text as="div" className="remove-child-field-label">
            Search child
          </Form.Text>
          <Form.Control
            type="search"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Start typing a child name"
            aria-label="Search child"
          />
          <Form.Text className="text-muted">
            Search children in your classes.
          </Form.Text>
        </Form.Group>

        <div className="remove-child-results" aria-label="Matching children">
          {isSearching ? (
            <div className="remove-child-search-state">
              <Spinner animation="border" size="sm" />
              <span>Loading child search...</span>
            </div>
          ) : trimmedSearchTerm.length < STUDENT_SEARCH_MIN_LENGTH ? (
            <p className="remove-child-muted">
              Enter at least 2 characters to search your classes.
            </p>
          ) : selectedChild && !isDisambiguating ? (
            <button
              type="button"
              key={`${selectedChild.classId}-${selectedChild.id}`}
              className={[
                "remove-child-result",
                `status-${selectedChild.screenerStatus}`,
                "selected"
              ].join(" ")}
              onClick={() => handleChildSelect(selectedChild)}
              aria-label={`Select ${selectedChild.name} from ${selectedChild.classDisplayName}. ${getScreenerStatusLabel(selectedChild.screenerStatus)}`}
              aria-pressed
            >
              <span className="remove-child-result-copy">
                <strong>{selectedChild.name}</strong>
                <span className="remove-child-result-class">
                  {[selectedChild.className, selectedChild.grade, selectedChild.academicYear]
                    .filter(Boolean)
                    .join(" - ") || selectedChild.classDisplayName}
                </span>
                <span className={`remove-child-status status-${selectedChild.screenerStatus}`}>
                  {getScreenerStatusLabel(selectedChild.screenerStatus)}
                </span>
              </span>
            </button>
          ) : matchingChildren.length > 0 ? (
            matchingChildren.map((child) => {
              const isSelected = selectedChild?.id === child.id;
              const statusLabel = getScreenerStatusLabel(child.screenerStatus);

              return (
                <button
                  type="button"
                  key={`${child.classId}-${child.id}`}
                  className={[
                    "remove-child-result",
                    `status-${child.screenerStatus}`,
                    isSelected ? "selected" : ""
                  ].filter(Boolean).join(" ")}
                  onClick={() => handleChildSelect(child)}
                  aria-label={`Select ${child.name} from ${child.classDisplayName}. ${statusLabel}`}
                  aria-pressed={isSelected}
                >
                  <span className="remove-child-result-copy">
                    <strong>{child.name}</strong>
                    <span className="remove-child-result-class">
                      {[child.className, child.grade, child.academicYear]
                        .filter(Boolean)
                        .join(" - ") || child.classDisplayName}
                    </span>
                    <span className={`remove-child-status status-${child.screenerStatus}`}>
                      {statusLabel}
                    </span>
                  </span>
                </button>
              );
            })
          ) : (
            <p className="remove-child-muted">
              No matching children found in your classes.
            </p>
          )}
        </div>

        {selectedChild && (
          <>
            <p className="remove-child-selection-note">
              {selectedChild.name} is in {selectedChild.className}.
            </p>
            {selectedChildWarning && (
              <p className="remove-child-selection-warning">
                {selectedChildWarning}
              </p>
            )}
          </>
        )}

        <Form.Group className="mt-4" controlId="removeChildReason">
          <Form.Label>Why is {reasonSubject} being removed?</Form.Label>
          <div className="remove-child-reasons">
            {REMOVE_REASONS.map((reason) => (
              <label
                key={reason}
                className={`remove-child-reason${removeReason === reason ? " selected" : ""}`}
              >
                <Form.Check
                  type="radio"
                  name="removeChildReason"
                  checked={removeReason === reason}
                  onChange={() => setRemoveReason(reason)}
                  aria-label={reason}
                />
                <span>{reason}</span>
              </label>
            ))}
          </div>
          {removeReason === OTHER_REASON && (
            <Form.Group className="remove-child-other-reason" controlId="removeChildOtherReason">
              <Form.Label>Other reason</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={otherReason}
                onChange={(event) => setOtherReason(event.target.value)}
                placeholder="Tell us why this child should be removed"
                aria-label="Other remove reason"
              />
            </Form.Group>
          )}
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <span className="remove-child-footer-note">
          {selectedChild
            ? `${selectedChildFirstName} will no longer appear in ${selectedChild.className}.`
            : ""}
        </span>
        <Button variant="outline-secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="danger" disabled={!canRemove} onClick={removeChild}>
          {isRemoving ? "Removing..." : "Remove child"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderSuccess = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title>Child Removed</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="remove-child-intro">
          {selectedChild?.name} no longer appears in {selectedChild?.className}.
        </p>
        <Alert variant="success" className="remove-child-success">
          <FaCheckCircle aria-hidden="true" />
          <span>
            <strong>
              {selectedChild?.name} has been removed from {selectedChild?.className}.
            </strong>
            <span>{selectedChildFirstName} will no longer appear in this class list.</span>
          </span>
        </Alert>
        <div className="remove-child-next">
          <strong className="remove-child-next-title">What happens next</strong>
          <div className="remove-child-next-row">
            <span className="remove-child-next-step">Done</span>
            <span className="remove-child-next-detail">{embedded ? "Return to SENSAI" : "Return to Help Centre home"}</span>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <span className="remove-child-footer-note">{embedded ? "Returns to SENSAI" : "Returns to Help Centre home"}</span>
        <Button variant="primary" onClick={handleDone}>
          Done
        </Button>
      </Modal.Footer>
    </>
  );

  return (
    <Modal
      show={show}
      onHide={handleModalHide}
      centered
      backdrop="static"
      className="teacher-remove-child-modal"
    >
      {stage === "form" && renderForm()}
      {stage === "success" && renderSuccess()}
    </Modal>
  );
}

export default TeacherRemoveChildModal;
